import React from 'react';
import counsellorContent from '../../content/counsellors.json';
import { useLeadModal } from './LeadModalContext.js';

function initials(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

/**
 * Social proof built only from real, verified counsellor entries.
 * Renders nothing until at least one entry has `verified: true`.
 */
export default function CounsellorSection({ stats }) {
  const { openLeadModal } = useLeadModal();
  const counsellors = (counsellorContent.counsellors || []).filter(
    (entry) => entry && entry.verified === true && entry.name
  );

  if (counsellors.length === 0) {
    return null;
  }

  // Only figures that are true by construction: what the site actually lists.
  const trust = [];
  if (stats?.universities_count) trust.push(`${stats.universities_count} universities listed`);
  if (stats?.courses_count) trust.push(`${stats.courses_count} programmes compared`);
  const languages = Array.from(new Set(counsellors.flatMap((entry) => entry.languages || [])));
  if (languages.length) trust.push(`Guidance in ${languages.join(', ')}`);

  return (
    <section className="section counsellor-section" aria-labelledby="counsellor-heading">
      <div className="container">
        <header className="section-header">
          <h2 id="counsellor-heading">{counsellorContent.heading}</h2>
          <p>{counsellorContent.intro}</p>
        </header>

        {trust.length > 0 && (
          <ul className="trust-strip">
            {trust.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}

        <div className="counsellor-grid">
          {counsellors.map((entry) => (
            <article className="counsellor-card" key={entry.id || entry.name}>
              {entry.photo ? (
                <img
                  className="counsellor-photo"
                  src={entry.photo}
                  alt={`Counsellor ${entry.name}`}
                  width="96"
                  height="96"
                  loading="lazy"
                />
              ) : (
                <span className="counsellor-photo counsellor-initials" aria-hidden>
                  {initials(entry.name)}
                </span>
              )}
              <h3>{entry.name}</h3>
              {entry.role && <p className="counsellor-role">{entry.role}</p>}
              <ul className="counsellor-facts">
                {entry.experienceYears > 0 && <li>{entry.experienceYears}+ years advising students</li>}
                {entry.languages?.length > 0 && <li>Speaks {entry.languages.join(', ')}</li>}
                {entry.focus?.length > 0 && <li>Focus: {entry.focus.join(', ')}</li>}
              </ul>
              {entry.bio && <p className="counsellor-bio">{entry.bio}</p>}
            </article>
          ))}
        </div>

        <div className="counsellor-cta">
          <button
            type="button"
            className="cta-button"
            onClick={() => openLeadModal({ intent: 'counsel', leadSource: 'counsellor_section' })}
          >
            {counsellorContent.callbackCta}
          </button>
        </div>
      </div>
    </section>
  );
}
